import { WebViewMessageService } from '../integrations/webview/services/messageService';
import { WebViewMessageType } from '../integrations/webview/types';

const RECOVERY_DATA_KEY = 'tahta_webview_recovery';
const PENDING_ANSWERS_KEY = 'tahta_pending_answers';

export interface RecoveryData {
  testId: string;
  currentQuestionIndex: number;
  answers: unknown;
  timestamp: number;
  sessionData?: unknown;
}

export class WebViewRecoveryService {
  private static instance: WebViewRecoveryService;
  private recoveryCheckInterval?: NodeJS.Timeout;
  
  private constructor() {}
  
  static getInstance(): WebViewRecoveryService {
    if (!WebViewRecoveryService.instance) {
      WebViewRecoveryService.instance = new WebViewRecoveryService();
    }
    return WebViewRecoveryService.instance;
  }
  
  saveRecoveryData(data: RecoveryData): void {
    try {
      localStorage.setItem(RECOVERY_DATA_KEY, JSON.stringify(data));
      // Recovery data saved
    } catch (error) {
      console.error('Failed to save recovery data:', error);
    }
  }
  
  getRecoveryData(): RecoveryData | null {
    try {
      const data = localStorage.getItem(RECOVERY_DATA_KEY);
      if (!data) return null;
      
      const parsed = JSON.parse(data);
      
      // Check if data is not too old (24 hours)
      const isExpired = Date.now() - parsed.timestamp > 24 * 60 * 60 * 1000;
      if (isExpired) {
        this.clearRecoveryData();
        return null;
      }
      
      return parsed;
    } catch (error) {
      console.error('Failed to get recovery data:', error);
      return null;
    }
  }
  
  clearRecoveryData(): void {
    try {
      localStorage.removeItem(RECOVERY_DATA_KEY);
      localStorage.removeItem(PENDING_ANSWERS_KEY);
      // Recovery data cleared
    } catch (error) {
      console.error('Failed to clear recovery data:', error);
    }
  }
  
  async attemptRecovery(): Promise<boolean> {
    const recoveryData = this.getRecoveryData();
    if (!recoveryData) return false;
    
    try {
      const messageService = WebViewMessageService.getInstance();
      
      // Try to reconnect and send pending data
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          resolve(false);
        }, 5000);
        
        // Send recovery message
        const recoveryPayload = typeof recoveryData.answers === 'object' && recoveryData.answers !== null
          ? { ...recoveryData.answers, isRecovery: true }
          : { data: recoveryData.answers, isRecovery: true };
        
        messageService.send(WebViewMessageType.EXIT_REQUESTED, recoveryPayload);
        
        // Wait for confirmation
        const cleanup = messageService.addHandler(WebViewMessageType.TEST_COMPLETED, () => {
          clearTimeout(timeout);
          cleanup();
          this.clearRecoveryData();
          resolve(true);
        });
      });
    } catch (error) {
      console.error('Recovery attempt failed:', error);
      return false;
    }
  }
  
  startAutoRecovery(onSuccess?: () => void, onFail?: () => void): void {
    if (this.recoveryCheckInterval) {
      clearInterval(this.recoveryCheckInterval);
    }
    
    const attemptRecovery = async () => {
      const success = await this.attemptRecovery();
      if (success) {
        this.stopAutoRecovery();
        onSuccess?.();
      } else {
        onFail?.();
      }
    };
    
    // Try every 10 seconds
    this.recoveryCheckInterval = setInterval(attemptRecovery, 10000);
    
    // Try immediately
    attemptRecovery();
  }
  
  stopAutoRecovery(): void {
    if (this.recoveryCheckInterval) {
      clearInterval(this.recoveryCheckInterval);
      this.recoveryCheckInterval = undefined;
    }
  }
}

export const saveAnswersForRecovery = (testId: string, questionIndex: number, answers: unknown) => {
  const recoveryService = WebViewRecoveryService.getInstance();
  recoveryService.saveRecoveryData({
    testId,
    currentQuestionIndex: questionIndex,
    answers,
    timestamp: Date.now()
  });
};

export const checkAndRecoverSession = async (): Promise<RecoveryData | null> => {
  const recoveryService = WebViewRecoveryService.getInstance();
  const data = recoveryService.getRecoveryData();
  
  if (data) {
    // Found recovery data from timestamp
  }
  
  return data;
};